/**
 * src/admin-ui/src/theme/theme.ts
 *
 * Central MUI theme built from the warm any-classifier palette.
 */

import { createTheme, Theme } from '@mui/material/styles';

export type ColorMode = 'light' | 'dark';

const sharedPalette = {
  primary: {
    main: '#D2691E',
    dark: '#B85714',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#8B4513',
    light: '#CD853F',
    contrastText: '#ffffff',
  },
  success: { main: '#22c55e' },
  warning: { main: '#f59e0b' },
  error: { main: '#e2562b' },
  info: { main: '#0891b2' },
};

/**
 * Build the application theme for a color mode.
 *
 * :param mode: Active light or dark color mode.
 * :returns: Configured MUI theme.
 */
export function createAppTheme(mode: ColorMode): Theme {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      ...sharedPalette,
      background: {
        default: isDark ? '#261a10' : '#faf6f1',
        paper: isDark ? '#1c130c' : '#ffffff',
      },
      text: {
        primary: isDark ? '#f5ede4' : '#1a0f08',
        secondary: isDark ? '#c8a98a' : '#5c3d2e',
        disabled: isDark ? '#8a6550' : '#a07860',
      },
      divider: isDark ? '#4a2e1a' : '#e6d8c9',
      action: {
        hover: isDark ? '#332416' : '#f3ebe2',
        selected: isDark ? '#4a2e1a' : '#f3ebe2',
      },
    },
    spacing: 8,
    shape: {
      borderRadius: 0,
    },
    typography: {
      fontFamily:
        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            colorScheme: mode,
          },
          body: {
            minWidth: 320,
          },
          '*, *::before, *::after': {
            boxSizing: 'border-box',
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: false,
        },
        styleOverrides: {
          root: {
            minHeight: 44,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
            backgroundColor: isDark ? '#332416' : '#f3ebe2',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: isDark ? '#e8894a' : '#D2691E',
          },
        },
      },
    },
  });
}
